import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  PLANS,
  GYM_AREAS,
  CONTACT_INFO,
} from "./GymData";


// =====================================
// HELPERS
// =====================================

export const getToday = () => {
  return new Date().toISOString().slice(0, 10);
};


export const formatMoney = (amount) => {
  return `₹${Number(amount || 0).toLocaleString("en-IN")}`;
};


export const formatDate = (date) => {

  if (!date) return "-";

  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};


export const generateId = () => {
  return Date.now().toString();
};


export const generateInvoice = () => {
  return `INV-${Date.now()}`;
};


export const generateReceipt = () => {
  return `REC-${Date.now()}`;
};


export const emptyMember = () => ({
  id: "",
  name: "",
  phone: "",
  email: "",
  plan: "Monthly",
  amount: 999,
  discount: 0,
  paid: 0,
  payable: 999,
  due: 999,
  status: "Due",
  joinDate: getToday(),
  invoice: generateInvoice(),
  receipt: generateReceipt(),
});


// =====================================
// THEME TOGGLE
// =====================================

function ThemeButton({
  darkMode,
  setDarkMode,
}) {
  return (
    <button
      className="theme-toggle"
      onClick={() => setDarkMode(!darkMode)}
    >
      {darkMode
        ? "☀ Day Version"
        : "🌙 Night Version"}
    </button>
  );
}


// =====================================
// AUTH PAGE
// =====================================

export function AuthPage({
  mode,
  onBack,
  onSuccess,
  darkMode,
  setDarkMode,
}) {

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");


  const submit = (e) => {

    e.preventDefault();

    if (!email || !password) {
      alert("Please enter email and password.");
      return;
    }

    if (mode === "signup" && !name) {
      alert("Please enter your name.");
      return;
    }


    const users = JSON.parse(
      localStorage.getItem("fitrackUsers") || "[]"
    );


    if (mode === "signup") {

      const alreadyExists = users.find(
        (user) => user.email === email
      );

      if (alreadyExists) {
        alert("Account already exists. Please login.");
        return;
      }


      const newUser = {
        name,
        email,
        password,
      };


      users.push(newUser);

      localStorage.setItem(
        "fitrackUsers",
        JSON.stringify(users)
      );

      localStorage.setItem(
        "fitrackCurrentUser",
        JSON.stringify(newUser)
      );

      onSuccess(newUser);

    } else {

      const user = users.find(
        (item) =>
          item.email === email &&
          item.password === password
      );


      if (!user) {
        alert("Invalid email or password.");
        return;
      }


      localStorage.setItem(
        "fitrackCurrentUser",
        JSON.stringify(user)
      );

      onSuccess(user);
    }
  };


  return (
    <div className="auth-page">

      <div className="auth-top">

        <button
          className="back-button"
          onClick={onBack}
        >
          ← Back
        </button>

        <ThemeButton
          darkMode={darkMode}
          setDarkMode={setDarkMode}
        />

      </div>


      <div className="auth-card">

        <div className="logo">
          FIT<span>RACK</span>
        </div>

        <p className="orange-text">
          {mode === "login"
            ? "WELCOME BACK"
            : "JOIN FITRACK"}
        </p>

        <h1>
          {mode === "login"
            ? "LOGIN"
            : "CREATE ACCOUNT"}
        </h1>


        <form onSubmit={submit}>

          {mode === "signup" && (
            <div className="form-group">

              <label>
                Full Name
              </label>

              <input
                type="text"
                placeholder="Enter your name"
                value={name}
                onChange={(e) =>
                  setName(e.target.value)
                }
              />

            </div>
          )}


          <div className="form-group">

            <label>
              Email
            </label>

            <input
              type="email"
              placeholder="Enter email"
              value={email}
              onChange={(e) =>
                setEmail(e.target.value)
              }
            />

          </div>


          <div className="form-group">

            <label>
              Password
            </label>

            <input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) =>
                setPassword(e.target.value)
              }
            />

          </div>


          <button
            type="submit"
            className="primary-button full-button"
          >
            {mode === "login"
              ? "LOGIN →"
              : "CREATE ACCOUNT →"}
          </button>

        </form>

      </div>

    </div>
  );
}


// =====================================
// TOP NAV
// =====================================

export function TopNav({
  currentUser,
  screen,
  setScreen,
  logout,
  darkMode,
  setDarkMode,
}) {

  return (
    <nav className="dashboard-nav">

      <div className="logo">
        FIT<span>RACK</span>
      </div>


      <div className="dashboard-links">

        <button
          className={screen === "dashboard" ? "active" : ""}
          onClick={() => setScreen("dashboard")}
        >
          Dashboard
        </button>

        <button
          className={screen === "members" ? "active" : ""}
          onClick={() => setScreen("members")}
        >
          Members
        </button>

        <button
          className={screen === "membership" ? "active" : ""}
          onClick={() => setScreen("membership")}
        >
          Membership
        </button>

        <button
          className={screen === "gym" ? "active" : ""}
          onClick={() => setScreen("gym")}
        >
          Gym
        </button>

        <button
          className={screen === "enquiry" ? "active" : ""}
          onClick={() => setScreen("enquiry")}
        >
          Enquire
        </button>

      </div>


      <div className="dashboard-actions">

        <ThemeButton
          darkMode={darkMode}
          setDarkMode={setDarkMode}
        />

        <span className="user-name">
          {currentUser?.name || "Admin"}
        </span>

        <button
          className="logout-button"
          onClick={logout}
        >
          Logout
        </button>

      </div>

    </nav>
  );
}


// =====================================
// FLOATING CONTACT
// =====================================

export function FloatingContact() {

  const whatsappMessage =
    encodeURIComponent(
      "Hello FITRACK! I want to know more about your gym."
    );


  return (
    <div className="floating-contact">

      <a
        href={`https://wa.me/${CONTACT_INFO.whatsapp}?text=${whatsappMessage}`}
        target="_blank"
        rel="noreferrer"
        className="floating-whatsapp"
      >
        💬
      </a>


      <a
        href={`tel:${CONTACT_INFO.phone1}`}
        className="floating-call"
      >
        📞
      </a>

    </div>
  );
}


// =====================================
// DASHBOARD
// =====================================

export function Dashboard({
  members,
  setScreen,
}) {

  const totalMembers = members.length;

  const paidMembers = members.filter(
    (member) => member.status === "Paid"
  ).length;

  const dueMembers = members.filter(
    (member) => member.status === "Due"
  ).length;

  const totalCollected = members.reduce(
    (sum, member) =>
      sum + Number(member.paid || 0),
    0
  );

  const totalDue = members.reduce(
    (sum, member) =>
      sum + Number(member.due || 0),
    0
  );


  return (
    <div className="dashboard-page">

      <div className="dashboard-title">

        <div>

          <p className="orange-text">
            FITRACK MANAGEMENT
          </p>

          <h1>
            DASHBOARD
          </h1>

        </div>


        <button
          className="primary-button"
          onClick={() => setScreen("add-member")}
        >
          + ADD MEMBER
        </button>

      </div>


      <div className="stats-grid">

        <Stat
          title="Total Members"
          value={totalMembers}
          icon="👥"
        />

        <Stat
          title="Paid Members"
          value={paidMembers}
          icon="✓"
        />

        <Stat
          title="Due Members"
          value={dueMembers}
          icon="!"
        />

        <Stat
          title="Collected"
          value={formatMoney(totalCollected)}
          icon="₹"
        />

        <Stat
          title="Total Due"
          value={formatMoney(totalDue)}
          icon="⚠"
        />

      </div>


      <div className="dashboard-info">

        <div className="info-card">

          <h2>
            Quick Actions
          </h2>

          <div className="quick-actions">

            <button
              onClick={() =>
                setScreen("add-member")
              }
            >
              + Add Member
            </button>

            <button
              onClick={() =>
                setScreen("members")
              }
            >
              👥 View Members
            </button>

            <button
              onClick={() =>
                setScreen("membership")
              }
            >
              💳 Membership Plans
            </button>

            <button
              onClick={() =>
                setScreen("gym")
              }
            >
              🏋 Gym Information
            </button>

          </div>

        </div>


        <div className="info-card">

          <h2>
            Fee Summary
          </h2>

          <p>
            Total collected:
            <strong>
              {formatMoney(totalCollected)}
            </strong>
          </p>

          <p>
            Total pending:
            <strong>
              {formatMoney(totalDue)}
            </strong>
          </p>

        </div>

      </div>

    </div>
  );
}


// =====================================
// STAT
// =====================================

function Stat({
  title,
  value,
  icon,
}) {

  return (
    <div className="stat-card">

      <div className="stat-icon">
        {icon}
      </div>

      <div>

        <p>{title}</p>

        <h2>{value}</h2>

      </div>

    </div>
  );
}


// =====================================
// MEMBERS PAGE
// =====================================

export function MembersPage({
  members,
  onAdd,
  onEdit,
  onInvoice,
  onDelete,
}) {

  const [search, setSearch] = useState("");


  const filteredMembers = useMemo(() => {

    return members.filter((member) => {

      const value =
        `${member.name} ${member.phone} ${member.email}`
          .toLowerCase();

      return value.includes(
        search.toLowerCase()
      );
    });

  }, [members, search]);


  return (
    <div className="dashboard-page">

      <div className="dashboard-title">

        <div>

          <p className="orange-text">
            MEMBERS
          </p>

          <h1>
            ALL MEMBERS
          </h1>

        </div>


        <button
          className="primary-button"
          onClick={onAdd}
        >
          + ADD MEMBER
        </button>

      </div>


      <input
        className="search-input"
        placeholder="Search member..."
        value={search}
        onChange={(e) =>
          setSearch(e.target.value)
        }
      />


      <div className="table-wrapper">

        <table>

          <thead>

            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Plan</th>
              <th>Payable</th>
              <th>Paid</th>
              <th>Due</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>

          </thead>


          <tbody>

            {filteredMembers.length === 0 ? (

              <tr>

                <td
                  colSpan="8"
                  className="empty-table"
                >
                  No members found.
                </td>

              </tr>

            ) : (

              filteredMembers.map((member) => (

                <tr key={member.id}>

                  <td>
                    <strong>
                      {member.name}
                    </strong>
                  </td>

                  <td>
                    {member.phone}
                  </td>

                  <td>
                    {member.plan}
                  </td>

                  <td>
                    {formatMoney(member.payable)}
                  </td>

                  <td>
                    {formatMoney(member.paid)}
                  </td>

                  <td>
                    {formatMoney(member.due)}
                  </td>

                  <td>

                    <span
                      className={`status-badge ${
                        member.status.toLowerCase()
                      }`}
                    >
                      {member.status}
                    </span>

                  </td>

                  <td>

                    <div className="table-actions">

                      <button
                        onClick={() =>
                          onEdit(member)
                        }
                      >
                        Edit
                      </button>

                      <button
                        onClick={() =>
                          onInvoice(member)
                        }
                      >
                        Invoice
                      </button>

                      <button
                        className="danger-button"
                        onClick={() =>
                          onDelete(member.id)
                        }
                      >
                        Delete
                      </button>

                    </div>

                  </td>

                </tr>

              ))

            )}

          </tbody>

        </table>

      </div>

    </div>
  );
}


// =====================================
// MEMBER FORM
// =====================================

export function MemberForm({
  initialMember,
  onSave,
  onCancel,
}) {

  const [form, setForm] = useState(
    initialMember || emptyMember()
  );


  useEffect(() => {

    const selectedPlan = PLANS.find(
      (plan) =>
        plan.name === form.plan
    );


    const planAmount =
      selectedPlan?.price || 0;

    const discount =
      Number(form.discount || 0);

    const paid =
      Number(form.paid || 0);

    const payable =
      Math.max(planAmount - discount, 0);

    const due =
      Math.max(payable - paid, 0);


    let status = "Due";

    if (paid >= payable && payable > 0) {
      status = "Paid";
    } else if (paid > 0) {
      status = "Partial";
    }


    setForm((previous) => ({
      ...previous,
      amount: planAmount,
      payable,
      due,
      status,
    }));

  }, [
    form.plan,
    form.discount,
    form.paid,
  ]);


  const update = (field, value) => {

    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

  };


  const submit = (e) => {

    e.preventDefault();

    if (!form.name || !form.phone) {
      alert("Name and phone are required.");
      return;
    }


    onSave({
      ...form,
      id: form.id || generateId(),
      invoice:
        form.invoice || generateInvoice(),
      receipt:
        form.receipt || generateReceipt(),
    });
  };


  return (
    <div className="dashboard-page">

      <div className="dashboard-title">

        <div>

          <p className="orange-text">
            MEMBER DETAILS
          </p>

          <h1>
            {form.id
              ? "EDIT MEMBER"
              : "ADD MEMBER"}
          </h1>

        </div>

      </div>


      <form
        className="member-form"
        onSubmit={submit}
      >

        <Field
          label="Full Name"
          value={form.name}
          onChange={(value) =>
            update("name", value)
          }
          placeholder="Enter member name"
        />


        <Field
          label="Phone Number"
          value={form.phone}
          onChange={(value) =>
            update("phone", value)
          }
          placeholder="Enter phone number"
        />


        <Field
          label="Email"
          type="email"
          value={form.email}
          onChange={(value) =>
            update("email", value)
          }
          placeholder="Enter email"
        />


        <div className="form-group">

          <label>
            Membership Plan
          </label>

          <select
            value={form.plan}
            onChange={(e) =>
              update("plan", e.target.value)
            }
          >

            {PLANS.map((plan) => (

              <option
                key={plan.name}
                value={plan.name}
              >
                {plan.name} - ₹{plan.price}
              </option>

            ))}

          </select>

        </div>


        <Field
          label="Discount"
          type="number"
          value={form.discount}
          onChange={(value) =>
            update("discount", value)
          }
          placeholder="0"
        />


        <Field
          label="Paid Amount"
          type="number"
          value={form.paid}
          onChange={(value) =>
            update("paid", value)
          }
          placeholder="0"
        />


        <Field
          label="Join Date"
          type="date"
          value={form.joinDate}
          onChange={(value) =>
            update("joinDate", value)
          }
        />


        <div className="fee-preview">

          <div>
            <span>Plan Amount</span>
            <strong>
              {formatMoney(form.amount)}
            </strong>
          </div>

          <div>
            <span>Payable</span>
            <strong>
              {formatMoney(form.payable)}
            </strong>
          </div>

          <div>
            <span>Paid</span>
            <strong>
              {formatMoney(form.paid)}
            </strong>
          </div>

          <div>
            <span>Due</span>
            <strong>
              {formatMoney(form.due)}
            </strong>
          </div>

        </div>


        <div className="form-buttons">

          <button
            type="button"
            className="outline-button"
            onClick={onCancel}
          >
            CANCEL
          </button>

          <button
            type="submit"
            className="primary-button"
          >
            SAVE MEMBER →
          </button>

        </div>

      </form>

    </div>
  );
}


// =====================================
// FIELD
// =====================================

function Field({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
}) {

  return (
    <div className="form-group">

      <label>
        {label}
      </label>

      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) =>
          onChange(e.target.value)
        }
      />

    </div>
  );
}


// =====================================
// INVOICE PAGE
// =====================================

export function InvoicePage({
  member,
  onBack,
}) {

  if (!member) {
    return null;
  }


  return (
    <div className="invoice-page">

      <div className="invoice-actions">

        <button
          className="outline-button"
          onClick={onBack}
        >
          ← BACK
        </button>

        <button
          className="primary-button"
          onClick={() => window.print()}
        >
          PRINT INVOICE
        </button>

      </div>


      <div className="invoice-card print-area">

        <div className="invoice-header">

          <div>

            <div className="logo">
              FIT<span>RACK</span>
            </div>

            <p>
              {CONTACT_INFO.location}
            </p>

          </div>


          <div className="invoice-number">

            <strong>
              INVOICE
            </strong>

            <p>
              {member.invoice}
            </p>

            <p>
              {formatDate(member.joinDate)}
            </p>

          </div>

        </div>


        <div className="invoice-member">

          <h3>
            MEMBER DETAILS
          </h3>

          <p>
            <strong>Name:</strong>{" "}
            {member.name}
          </p>

          <p>
            <strong>Phone:</strong>{" "}
            {member.phone}
          </p>

          <p>
            <strong>Email:</strong>{" "}
            {member.email || "-"}
          </p>

          <p>
            <strong>Plan:</strong>{" "}
            {member.plan}
          </p>

        </div>


        <div className="invoice-table">

          <InvoiceRow
            label="Membership"
            value={formatMoney(member.amount)}
          />

          <InvoiceRow
            label="Discount"
            value={`-${formatMoney(member.discount)}`}
          />

          <InvoiceRow
            label="Payable"
            value={formatMoney(member.payable)}
          />

          <InvoiceRow
            label="Paid"
            value={formatMoney(member.paid)}
          />

          <InvoiceRow
            label="Remaining Due"
            value={formatMoney(member.due)}
          />

        </div>


        <div className="invoice-total">

          <span>
            TOTAL DUE
          </span>

          <strong>
            {formatMoney(member.due)}
          </strong>

        </div>


        <div className="invoice-footer">

          <p>
            Thank you for choosing FITRACK.
          </p>

          <p>
            Train. Improve. Repeat.
          </p>

        </div>

      </div>

    </div>
  );
}


// =====================================
// INVOICE ROW
// =====================================

function InvoiceRow({
  label,
  value,
}) {

  return (
    <div className="invoice-row">

      <span>
        {label}
      </span>

      <strong>
        {value}
      </strong>

    </div>
  );
}


// =====================================
// MEMBERSHIP PAGE
// =====================================

export function MembershipPage({
  onAdd,
}) {

  return (
    <div className="dashboard-page">

      <div className="dashboard-title">

        <div>

          <p className="orange-text">
            MEMBERSHIP
          </p>

          <h1>
            GYM PLANS
          </h1>

        </div>

        <button
          className="primary-button"
          onClick={onAdd}
        >
          + ADD MEMBER
        </button>

      </div>


      <div className="plans-grid">

        {PLANS.map((plan, index) => (

          <div
            className={`plan-card ${
              index === 1
                ? "featured-plan"
                : ""
            }`}
            key={plan.name}
          >

            {index === 1 && (
              <div className="popular-tag">
                POPULAR
              </div>
            )}

            <h3>
              {plan.name}
            </h3>

            <div className="plan-price">
              ₹{plan.price}
            </div>

            <p>
              {plan.duration}
            </p>

            <ul className="plan-list">

              <li>✓ Gym Access</li>
              <li>✓ Training Area</li>
              <li>✓ Basic Support</li>

            </ul>

            <button
              className="primary-button"
              onClick={onAdd}
            >
              SELECT PLAN
            </button>

          </div>

        ))}

      </div>

    </div>
  );
}


// =====================================
// GYM PAGE
// =====================================

export function GymPage() {

  return (
    <div className="dashboard-page">

      <div className="dashboard-title">

        <div>

          <p className="orange-text">
            FITRACK
          </p>

          <h1>
            GYM INFORMATION
          </h1>

        </div>

      </div>


      <div className="gym-info-grid">

        {GYM_AREAS.map((area, index) => (

          <div
            className="info-card gym-info-card"
            key={index}
          >

            <div className="area-number">
              0{index + 1}
            </div>

            <h2>
              {area.title}
            </h2>

            <p>
              {area.description}
            </p>

          </div>

        ))}

      </div>


      <div className="info-card gym-contact-card">

        <h2>
          CONTACT FITRACK
        </h2>

        <p>
          📍 {CONTACT_INFO.location}
        </p>

        <p>
          📞 {CONTACT_INFO.phone1}
        </p>

        <p>
          📞 {CONTACT_INFO.phone2}
        </p>

        <p>
          ✉ {CONTACT_INFO.email}
        </p>

      </div>

    </div>
  );
}